import React, { FC } from 'react';
import Lottie from 'lottie-react';
import { Entry } from 'contentful';
import {
  TypeCourseChapter,
  TypeCourseSubChapterFields,
} from '@/core/generated-types';
import { Collapsible, CourseSubChapter } from '@/components';
import { ProgressBar } from '@/components/common/ProgressBar';
import { CourseSubChapterProgressBar } from '@/components/courses/CoursePlayer/utils';
import { calculatePercentage } from '@/utils/calculations';
import {
  captureCourseChapterNavigationEvent,
  NAVIGATION_EVENT_TYPES,
} from '@/utils/dataLayer';
import { groovySoundWavesAnimation } from './assets/groovySoundWavesAnimation';

export interface CourseChapterProps {
  courseChapter: TypeCourseChapter;
  isActiveChapter: boolean;
  isFirstChapter: boolean;
  navigateToChapter: () => void;
  closeChapter: () => void;
  progressBars?: CourseSubChapterProgressBar[];
  isActiveVideoInChapter?: boolean;
  isVideoActive?: (videoSysId: string) => boolean;
  streamVideoOnDemand?: (videoSysId: string) => void;
  enablePreviewMode?: boolean;
  isVideoPlaying?: boolean;
  setIsVideoPlaying?: (bool: boolean) => void;
}

const CourseChapter: FC<CourseChapterProps> = ({
  courseChapter,
  isActiveChapter,
  isFirstChapter,
  navigateToChapter,
  closeChapter,
  isVideoActive,
  isActiveVideoInChapter,
  streamVideoOnDemand,
  progressBars,
  enablePreviewMode = false,
  isVideoPlaying = false,
  setIsVideoPlaying = () => undefined,
}) => {
  if (!courseChapter?.fields) {
    return null;
  }

  const { title, subChapters, description } = courseChapter?.fields;
  const isVideoPaused = !enablePreviewMode && !isVideoPlaying;

  const ChapterDescription = () => (
    <p className="mb:3 ml-7 max-w-xl pb-3 text-left text-base lg:ml-11">
      {description}
    </p>
  );

  const ChapterVisualAid = () => {
    if (!isActiveVideoInChapter) {
      const chapterProgressPercentage =
        _calculateChapterProgressPercentage(progressBars);

      return (
        <ProgressBar
          percentage={chapterProgressPercentage}
          styleVariant="radial"
        />
      );
    }

    // lottie schema description can be found here @link{https://lottiefiles.github.io/lottie-docs/schema/}
    const IN_FRAME = groovySoundWavesAnimation.ip;
    const OUT_FRAME = groovySoundWavesAnimation.op;
    const STOPPED_TO_START_TRANSITION_FRAME = 8;

    const pausedFrames = [IN_FRAME, STOPPED_TO_START_TRANSITION_FRAME];
    const playingFrames = [STOPPED_TO_START_TRANSITION_FRAME + 1, OUT_FRAME];
    const animationFrames = isVideoPaused ? pausedFrames : playingFrames;

    return (
      <Lottie
        animationData={groovySoundWavesAnimation}
        // silly but safe typecast: typescript infers the type number[] but lottie requires type of [number, number]
        initialSegment={animationFrames as [number, number]}
        id={`lottie-animation-${isVideoPaused ? 'paused' : 'playing'}`}
        loop
        autoPlay
      />
    );
  };

  function onOpen() {
    captureCourseChapterNavigationEvent(
      NAVIGATION_EVENT_TYPES.COURSE_CHAPTER_EXPANDED,
      {
        title,
      },
    );
    navigateToChapter();
  }

  function onClose() {
    captureCourseChapterNavigationEvent(
      NAVIGATION_EVENT_TYPES.COURSE_CHAPTER_COLLAPSED,
      {
        title,
      },
    );
    closeChapter();
  }

  if (enablePreviewMode) {
    return (
      <Collapsible
        title={title}
        onOpen={onOpen}
        onClose={onClose}
        collapsedStateOverrideValue={!isActiveChapter}
        classes="mx-4 lg:mx-0 lg:mr-8"
        hideTopDivider={isFirstChapter}
      >
        <ChapterDescription />
        {subChapters.map((subChapter) => (
          <CourseSubChapter
            key={subChapter.sys.id}
            courseSubChapter={subChapter}
            isPreview={!!enablePreviewMode}
            status={_getSubChapterStatus(
              enablePreviewMode,
              subChapter.sys.id,
              isVideoActive,
            )}
            isVideoPlaying={isVideoPlaying}
            setIsVideoPlaying={setIsVideoPlaying}
          />
        ))}
      </Collapsible>
    );
  }

  return (
    <Collapsible
      title={title}
      onOpen={onOpen}
      onClose={onClose}
      collapsedStateOverrideValue={!isActiveChapter}
      classes="mx-4 lg:mx-0 lg:mr-8"
      titleSupplementaryJSX={<ChapterVisualAid />}
      hideTopDivider={isFirstChapter}
    >
      <ChapterDescription />
      {subChapters.map((subChapter, i) => {
        const currentChapterStatus = _getSubChapterStatus(
          enablePreviewMode,
          subChapter.sys.id,
          isVideoActive,
        );

        const prevChapterStatus = _getSubChapterStatus(
          enablePreviewMode,
          subChapters[i - 1]?.sys.id,
          isVideoActive,
        );

        return (
          <CourseSubChapter
            key={subChapter.sys.id}
            courseSubChapter={subChapter}
            streamVideoOnDemand={streamVideoOnDemand}
            status={currentChapterStatus}
            percentComplete={_findProgressBar(progressBars, subChapter)}
            isPreviousSubChapterStreaming={prevChapterStatus === 'streaming'}
            isVideoPlaying={isVideoPlaying}
            setIsVideoPlaying={setIsVideoPlaying}
          />
        );
      })}
    </Collapsible>
  );
};

function _getSubChapterStatus(
  isPreview: boolean | undefined,
  subChapterId: string,
  isVideoActive?: (videoSysId: string) => boolean,
) {
  if (isPreview) {
    return 'locked';
  }

  if (isVideoActive && isVideoActive(subChapterId)) {
    return 'streaming';
  }

  return 'inactive';
}

function _findProgressBar(
  progressBars: CourseSubChapterProgressBar[] | undefined,
  subChapter: Entry<TypeCourseSubChapterFields>,
) {
  const progressBar =
    progressBars &&
    progressBars.find(
      (progressBar) => progressBar?.subChapterId === subChapter?.sys?.id,
    );

  if (!progressBar) {
    return 0;
  }

  return progressBar.percentageComplete;
}

function _calculateChapterProgressPercentage(
  progressBars: CourseSubChapterProgressBar[] | undefined,
) {
  if (!progressBars) return 0;

  const completedSubChapters = progressBars.filter(
    (progressBar) => progressBar.isComplete,
  );

  return calculatePercentage(
    completedSubChapters.length,
    progressBars.length,
    'floor',
  );
}

export default CourseChapter;
